import loadable from '@loadable/component';
import React, { useCallback, useState, memo } from 'react';

import { useManualPortfolioContext } from '../../../../PortfolioManual';
import { useIsMobileScreen } from '@/hooks/useMedia';
import { usePortfolio } from '@/hooks/portfolio/usePortfolio';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useDemoPortfolio } from '@/hooks/portfolio/useDemoPortfolio';

import type { EditableHoldingFields } from '../../../../PortfolioManual';
import type { Holding, Portfolio } from '@/hooks/portfolio/usePortfolio';
import type {
  DemoHolding,
  DemoPortfolio,
} from '@/hooks/portfolio/useDemoPortfolio';

import { useSortedUnmatchedHoldings } from '../../../../../hooks/useHoldingsSorting';
import { usePortfolioType } from '../../../hooks';

import { Table, DemoTable } from '../../Table';
import type { RowProps, RowExpandedProps } from '../../HoldingsTableRow';

const PartiallyRestrictionObserver = loadable(
  () => import('./PartiallyRestrictionUpgradeModal'),
  {
    resolveComponent: (c) => c.PartiallyRestrictionObserver,
  }
);

const PartiallyRestrictionUpgradeModal = loadable(
  () => import('./PartiallyRestrictionUpgradeModal'),
  {
    resolveComponent: (c) => c.PartiallyRestrictionUpgradeModal,
  }
);

const HoldingRow = memo(
  loadable(() => import('../../HoldingsTableRow'), {
    resolveComponent: (c) => c.HoldingRow,
  })
);
const DemoHoldingRow = memo(
  loadable(() => import('../../HoldingsTableRow'), {
    resolveComponent: (c) => c.DemoHoldingRow,
  })
);
const HoldingRowExpanded = loadable(() => import('../../HoldingsTableRow'), {
  resolveComponent: (c) => c.HoldingRowExpanded,
});
const DemoHoldingRowExpanded = loadable(
  () => import('../../HoldingsTableRow'),
  {
    resolveComponent: (c) => c.DemoHoldingRowExpanded,
  }
);

const RowSkeleton = loadable(() => import('../../HoldingsTableRow'), {
  resolveComponent: (c) => c.RowSkeleton,
});
const UnmatchedHoldingRowDesktop = loadable(
  () => import('../../UnmatchedHoldingRow')
);
const UnmatchedHoldingRowMobile = loadable(
  () => import('../../UnmatchedHoldingRow'),
  { resolveComponent: (c) => c.UnmatchedHoldingRowMobile }
);

const ExcludedHoldingRows = loadable(
  () => import('./TableExcludedHoldingRows'),
  { resolveComponent: (c) => c.TableExcludedHoldingRows }
);

type PopulatedStateProps = {
  portfolio: Portfolio | DemoPortfolio;
  holdingId: Holding['id'] | null;
  onHoldingSelect: (id: Holding['id'] | null) => void;
  holdings: Holding[] | DemoHolding[];
  row: React.ComponentType<RowProps>;
  rowExpanded: React.ComponentType<RowExpandedProps>;
};

export const LoadingState = () => {
  return (
    <>
      <RowSkeleton />
      <RowSkeleton />
      <RowSkeleton />
      <RowSkeleton />
      <RowSkeleton />
    </>
  );
};

const Rows = ({
  portfolio,
  onHoldingSelect,
  holdingId,
  holdings,
  row: Row,
  rowExpanded: RowExpanded,
}: PopulatedStateProps) => {
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const unmatchedHoldings = useSortedUnmatchedHoldings(
    portfolio?.link.unmatchedHoldings
  );

  const { updatePortfolio } = useManualPortfolioContext();

  const handleUpdateHolding = useCallback(
    (holdingId: Holding['id'], holdingFieldToUpdate: EditableHoldingFields) =>
      updatePortfolio({
        portfolioId: portfolio.id,
        holdingId,
        holdingFieldToUpdate,
      }),
    [portfolio.id, updatePortfolio]
  );

  const portfolioType = usePortfolioType(portfolio);

  return (
    <>
      {[
        ...holdings.map((holding, index) =>
          !isMobileScreen && holdingId ? (
            <Row
              index={index}
              key={holding.id}
              portfolioId={portfolio.id}
              selected={holdingId === holding.id}
              portfolioType={portfolioType}
              holding={holding}
              onHoldingSelect={onHoldingSelect}
              data-cy-id={`holdings-table-row-${holding.id}`}
            />
          ) : (
            <RowExpanded
              key={holding.id}
              portfolioId={portfolio.id}
              portfolioType={portfolioType}
              holding={holding}
              onHoldingSelect={onHoldingSelect}
              onUpdateHolding={handleUpdateHolding}
              data-cy-id={`holdings-table-row-${holding.id}`}
            />
          )
        ),
        ...unmatchedHoldings.map((unmatchedHolding) =>
          isMobileScreen ? (
            <UnmatchedHoldingRowMobile
              key={unmatchedHolding.id}
              unmatchedHolding={unmatchedHolding}
              data-cy-id={`holdings-table-unmatchedHolding-row-${unmatchedHolding.id}`}
            />
          ) : (
            <UnmatchedHoldingRowDesktop
              key={unmatchedHolding.id}
              unmatchedHolding={unmatchedHolding}
              data-cy-id={`holdings-table-unmatchedHolding-row-${unmatchedHolding.id}`}
            />
          )
        ),
      ]}
    </>
  );
};

type Props = {
  portfolioId: Portfolio['id'];
  holdingId: Holding['id'] | null;
  onHoldingSelect: (id: Holding['id'] | null) => void;
  holdings: Holding[] | DemoHolding[];
  excludedHoldings: Holding[] | DemoHolding[];
};

export const HoldingsTable = ({
  portfolioId,
  onHoldingSelect,
  holdingId,
  holdings,
  excludedHoldings,
}: Props) => {
  const { data: portfolio, status } = usePortfolio({ id: portfolioId });

  const [showRestrictionPlanModal, setShowRestrictionPlanModal] =
    useState(false);

  if (status === 'pending') {
    return (
      <Table holdingId={holdingId}>
        <LoadingState />
      </Table>
    );
  }

  if (status === 'success' && typeof portfolio !== 'undefined') {
    return (
      <>
        <Table holdingId={holdingId}>
          <Rows
            portfolio={portfolio}
            holdings={holdings}
            holdingId={holdingId}
            onHoldingSelect={onHoldingSelect}
            row={HoldingRow}
            rowExpanded={HoldingRowExpanded}
          />
          {excludedHoldings.length > 0 && (
            <>
              <PartiallyRestrictionObserver
                handleIntersecting={() => setShowRestrictionPlanModal(true)}
              />
              <ExcludedHoldingRows
                portfolio={portfolio}
                excludedHoldings={excludedHoldings}
                holdingId={holdingId}
                onHoldingSelect={onHoldingSelect}
              />
            </>
          )}
        </Table>
        {showRestrictionPlanModal && (
          <PartiallyRestrictionUpgradeModal portfolio={portfolio} />
        )}
      </>
    );
  }

  return null;
};

export const DemoHoldingsTable = ({
  portfolioId,
  onHoldingSelect,
  holdingId,
  holdings,
  excludedHoldings,
}: Props) => {
  const { data: portfolio, status } = useDemoPortfolio({ id: portfolioId });

  if (status === 'pending') {
    return (
      <DemoTable holdingId={holdingId}>
        <LoadingState />
      </DemoTable>
    );
  }

  if (status === 'success' && typeof portfolio !== 'undefined') {
    return (
      <DemoTable holdingId={holdingId}>
        <Rows
          portfolio={portfolio}
          holdings={holdings}
          holdingId={holdingId}
          onHoldingSelect={onHoldingSelect}
          row={DemoHoldingRow}
          rowExpanded={DemoHoldingRowExpanded}
        />
        {excludedHoldings.length > 0 && (
          <ExcludedHoldingRows
            portfolio={portfolio}
            excludedHoldings={excludedHoldings}
            holdingId={holdingId}
            onHoldingSelect={onHoldingSelect}
          />
        )}
      </DemoTable>
    );
  }

  return null;
};
